<template>
  <v-app id="app">
    <TheCabinetOff
      v-if="userCreateError"
      title="Техническая ошибка"
      :subtitle="`Возникла ошибка при создании пользователя add = ${userCreateError.code} ${userCreateError.time}, обратитесь в службу поддержки`"
      :details="userCreateError.message"
    />
    <BaseFlex
      v-else-if="!isInitialized"
      class="m_justify-center m_flex-grow1 m_align-center m_flex-column m_gap16"
    >
      <AppPageLoader />
    </BaseFlex>
    <TheCabinetOff v-else-if="fatalError" />
    <div
      v-else
      class="main"
    >
      <div class="wrapper">
        <div
          id="layout"
          class="layout"
        >
          <slot name="header" />
          <slot name="banner" />
          <div
            v-if="!$slots.banner"
            :key="`wrapper_${hasActiveCard}`"
            class="layout__wrapper"
            :class="{ 'layout__wrapper--with-card': hasActiveCard }"
          >
            <slot
              v-if="isShowNavbar"
              name="navbar"
            />
            <p
              v-if="error || isCapturedError"
              class="text-h5 text-center px-4 m_color-error"
            >
              {{ $t('general.error_occured') }}. {{ $t('general.try_again') }}
            </p>
            <slot
              v-else
              name="main"
            />
          </div>
          <slot name="footer" />
        </div>
      </div>
    </div>
    <NewYearAlert v-if="isInitialized" />
    <AppInfoDialog />
    <AppYellowModal />
    <BaseDialog
      v-model="productInModalOpened"
      content-width="1140px"
      @click:outside="onCloseProduct"
    >
      <Transition name="fade">
        <BaseCard
          v-if="productInModalOpened"
          class="m_bg-white m_pa16 m_pt8"
        >
          <Suspense>
            <template #fallback>
              <BaseSkeleton
                type="image"
                height="400"
              />
            </template>
            <component
              :is="$options.AppProductPageAsync"
              :key="`product-in-modal-${productInModalId}`"
              is-in-modal
              :product-id="productInModalId"
              @back="productInModalOpened = false"
            />
          </Suspense>
        </BaseCard>
      </Transition>
    </BaseDialog>
  </v-app>
</template>

<script lang="ts">
import '@project/assets/styles/components/layout/Default.scss';
import {
  computed,
  defineComponent,
  ref,
  watch,
} from 'vue';
import BaseFlex from '@/shared/lib/uikit/components/BaseFlex/BaseFlex.vue';
import {
  handleRuntimeError,
  RuntimeError,
} from '@/shared/lib/core/errors';
import AppPageLoader from '@/shared/lib/uikit/components/AppPageLoader/AppPageLoader.vue';
import { profileStoreActiveCard } from '@/shared/lib/domains/profile/profileStoreActiveCard';
import {
  useRouter,
  useRoute,
} from '@intlify/vue-router-bridge';
import { configEnv } from '@/shared/config-env';
import { useUiStore } from '@/shared/store/useUiStore';
import AppInfoDialog from '@/shared/components/AppInfoDialog/AppInfoDialog.vue';
import BaseDialog from '@/shared/lib/uikit/components/BaseDialog/BaseDialog.vue';
import BaseCard from '@/shared/lib/uikit/components/BaseCard/BaseCard.vue';
import { browserHistory } from '@/shared/modulesHigh/browser/history';
import {
  isProductWasOpened,
  productInModalOpened,
} from '@/shared/store/common';
import AppYellowModal from '@/shared/components/AppYellowModal/AppYellowModal.vue';
import BaseSkeleton from '@/shared/lib/uikit/components/BaseSkeleton/BaseSkeleton.vue';
import { AppProductPageAsync } from '@/shared/components/AppProductPage/AppProductPageAsync';
import TheCabinetOff from '@project/components/TheCabinetOff/TheCabinetOff.vue';
import { appState } from '@/shared/appState';
import { captureExceptionSentry } from '@/shared/composition/useSentry';
import NewYearAlert from '@/shared/components/Warnings/NewYearAlert.vue';

export default defineComponent({
  name: 'AppLayout',
  AppProductPageAsync,
  components: {
    AppYellowModal,
    TheCabinetOff,
    BaseCard,
    BaseDialog,
    AppInfoDialog,
    AppPageLoader,
    BaseFlex,
    BaseSkeleton,
    NewYearAlert,
  },
  props: {
    isInitialized: {
      type: Boolean,
      default: false,
    },
    error: {
      type: Boolean,
      default: false,
    },
  },
  setup() {
    const router = useRouter();
    const productInModalId = ref<number | null>(null);

    const onCloseProduct = () => {
      browserHistory.push('/my/catalog/products');
    };

    router.beforeEach((to, from, next) => {
      if (to.name === 'Catalog') {
        setTimeout(() => {
          router.push({ name: 'Products' }).catch(() => {});
        }, 50);
      }
      if (from.name !== 'Product' && to.name === 'Product' && to.params.id) {
        productInModalId.value = Number(to.params.id);
        productInModalOpened.value = true;
        browserHistory.push('/my' + to.path);
        isProductWasOpened.value = true;
      } else {
        productInModalOpened.value = false;
        next();
      }
    });

    watch(productInModalOpened, isOpened => {
      if (!isOpened) {
        onCloseProduct();
      }
    });

    const uiStore = useUiStore();

    return {
      onCloseProduct,
      productInModalId,
      productInModalOpened,
      fatalError: computed(() => uiStore.isFatalError),
      // Не смогли создать пользователя в БУС - кабинет работать не может
      userCreateError: computed(() => uiStore.userCreateError),
      appState,
    };
  },
  data() {
    return {
      isCapturedError: false,
    };
  },
  computed: {
    isShowNavbar() {
      const uiStore = useUiStore();
      return uiStore.hasNavbar;
    },
    hasActiveCard: profileStoreActiveCard,
  },
  watch: {
    $route: {
      handler(newValue) {
        const uiStore = useUiStore();
        uiStore.routeChanged(newValue);
      },
      immediate: true,
    },
  },
  updated() {
    const uiStore = useUiStore();
    uiStore.setIsErrorBanner(!!this.$slots.banner);
  },
  mounted() {
    const router = useRouter();
    router.afterEach(() => {
      this.isCapturedError = false;
    });
  },
  errorCaptured(err) {
    const router = useRouter();
    const route = useRoute();

    captureExceptionSentry(err, 'errorCaptured in AppLayout');
    if (configEnv.VUE_APP_ENVIRONMENT === 'development') {
      console.error(err);
    }
    if (err instanceof RuntimeError) {
      handleRuntimeError(err);

      if (route.name !== 'Profile') {
        router.push({ name: 'Profile' });
      }

      return false;
    }

    this.isCapturedError = true;

    return false;
  },
});
</script>

<style lang="scss">
body .v-progress-linear__indeterminate {
  background-color: var(--pageAccentBackground) !important;
}
</style>
